import {
    clearDeletedToolImageMemory,
    ensureNewlyAddedToolImagesLinked,
    ensureToolImagesLinkedInDocument,
    findDeletedToolMaImageNodeUuids,
} from '@/lib/sync-tool-builder-images';
import { unlinkImageFromDocument } from '@/lib/zybooks-api';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Extension } from '@tiptap/react';

const MA_IMAGE_NODE_TYPES = new Set(['image', 'zyInlineImage']);

function isMaImageNode(node: PMNode): boolean {
    return MA_IMAGE_NODE_TYPES.has(node.type.name);
}

function collectInlineMaImageNodeIds(doc: PMNode): Set<string> {
    const nodeIds = new Set<string>();
    doc.descendants(node => {
        if (!isMaImageNode(node)) {
            return;
        }
        const maImageNodeId = node.attrs?.maImageNodeId;
        if (typeof maImageNodeId === 'string' && maImageNodeId !== '') {
            nodeIds.add(maImageNodeId);
        }
    });
    return nodeIds;
}

export function findDeletedMaImageNodeIds(oldDoc: PMNode, newDoc: PMNode): string[] {
    const oldIds = collectInlineMaImageNodeIds(oldDoc);
    const newIds = collectInlineMaImageNodeIds(newDoc);
    return [...oldIds].filter(id => !newIds.has(id));
}

function getMaDocumentId(doc: PMNode): number | null {
    const documentId = doc.attrs?.documentId;
    if (typeof documentId === 'number' && documentId > 0) {
        return documentId;
    }
    return null;
}

function unlinkMaImageNodeUuids(maDocumentId: number, maImageNodeUuids: string[]): void {
    for (const maImageNodeUuid of maImageNodeUuids) {
        void unlinkImageFromDocument(maImageNodeUuid, maDocumentId).catch(error => {
            console.error('Failed to unlink MA image from document after node removal:', error);
        });
    }
}

export function handleDeletedMaImageLinks(oldDoc: PMNode, newDoc: PMNode): void {
    const maDocumentId = getMaDocumentId(newDoc);
    if (maDocumentId == null) {
        return;
    }

    const deletedUuids = [...findDeletedMaImageNodeIds(oldDoc, newDoc), ...findDeletedToolMaImageNodeUuids(oldDoc, newDoc)];
    unlinkMaImageNodeUuids(maDocumentId, deletedUuids);
}

export const MaImageDocumentSync = Extension.create({
    name: 'maImageDocumentSync',

    onTransaction({ transaction }) {
        if (!transaction.docChanged) {
            return;
        }

        clearDeletedToolImageMemory(this.editor, transaction.before, transaction.doc);
        handleDeletedMaImageLinks(transaction.before, transaction.doc);
        void ensureNewlyAddedToolImagesLinked(this.editor, transaction.before, transaction.doc);
    },

    onCreate() {
        void ensureToolImagesLinkedInDocument(this.editor);
    },
});
